import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { isEqual } from "lodash";
import { QueryDescription } from "common/utils/queryBuilder";

import Modal from "../../../Modal";
import {
  SavedView,
  useUpdateSavedViewMutation,
} from "../../../../../generated/graphql";
import { useStatusToasts } from "../../../../../hooks/useStatusToasts";
import { Text } from "../../../../Inputs/react-hook-form";
import { TableContext } from "../../TableContext";
import { track } from "../../../../../utils/tracking";

import { InputRow } from "../../../__styles__/Modal";
import { CommonModal } from "../../../CommonModal";
import { Body } from "../../../Typography";

interface EditSavedViewForm {
  name: string;
}

const EditSavedViewModal = ({
  query,
  hideModal,
  savedView,
}: {
  query?: QueryDescription;
  hideModal: () => void;
  savedView: SavedView;
}) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const [updateSavedView, { loading }] = useUpdateSavedViewMutation({
    onCompleted: () => {
      hideModal();
      addSuccessToast(`Your table view was successfully edited`);
    },
    onError: () => {
      addErrorToast(
        `There was an issue editing your table view. Please try again. If the problem persists, please email us at support@withforerunner.com`
      );
    },
  });

  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
  } = useForm<EditSavedViewForm>();

  const hasQueryChanges = !isEqual(query, savedView.query);

  const onSubmit = async (formData: EditSavedViewForm) => {
    const data = {
      ...formData,
      ...{ query: hasQueryChanges ? query : undefined },
      id: savedView.id,
    };

    track("Edited table view", {
      tableType: savedView.tableType,
      name: data.name,
    });

    await updateSavedView({ variables: { data } });
  };

  return (
    <CommonModal
      title="Edit view"
      primaryButtonText="Save view"
      onSubmit={handleSubmit(onSubmit)}
      disabled={!isValid}
      loading={loading}
      onCancel={hideModal}
      size="large"
      content={
        <>
          <Body size="default" type="regular">
            Any changes saved to this existing view will be updated for
            everyone. If filters or columns that reference sensitive data are
            present, only users with permissions will be able to see this view.
          </Body>
          <InputRow css={{ marginTop: "18px" }}>
            <Text
              {...register("name", { required: true })}
              error={errors.name?.message}
              size="small"
              label="Name"
              required
              defaultValue={savedView.name}
            />
          </InputRow>
        </>
      }
    />
  );
};

export const useEditSavedViewModal = () => {
  const { currentQuery } = useContext(TableContext);
  const [savedView, setSavedView] = useState<SavedView | null>(null);

  let [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <EditSavedViewModal
          hideModal={() => {
            hideModal();
            setSavedView(null);
          }}
          savedView={savedView!}
          query={currentQuery}
        />
      </Modal>
    ),
    [savedView, currentQuery]
  );

  const showEditSavedViewModal = (savedView: SavedView) => {
    setSavedView(savedView);
    showModal();
  };

  return showEditSavedViewModal;
};
